import type { RegistrationUserDataPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { type DeutschkursStammdaten } from '../stammdaten/deutschkurs-stammdaten-plugin.js';
import { renderIcsrmFields, renderIcsrmImpactMeasurementSection } from './icsrm-registration-user-data-plugin.js';

const countWords = (text: string | null | undefined): number =>
  (text ?? '').trim().split(/\s+/).filter(Boolean).length;

type WordCountedTextareaProps = {
  stContr: GenericFieldFormController<DeutschkursStammdaten>;
  fieldKey: 'motivationText' | 'scheduleText';
  placeholder: string;
  min: number;
  max: number;
};

const WordCountedTextarea = stateful<WordCountedTextareaProps>(({ stContr, fieldKey, placeholder, min, max }) => {
  const [count, setCount] = useState<number>(countWords(stContr.document?.[fieldKey] ?? ''));

  const outOfRange = count < min || count > max;

  return html`
    <x-flex space="xs">
      <x-textarea
        placeholder=${placeholder}
        ${stContr.field(fieldKey)}
        @input=${(e: Event) => {
          const el = e.composedPath().find((n: any) => n?.tagName === 'TEXTAREA') as HTMLTextAreaElement | undefined;
          const text = el?.value ?? '';
          // Mirror onto the document so the field() directive's re-render doesn't reset the field (x-textarea only emits value-picked on blur).
          if (stContr.document) (stContr.document as any)[fieldKey] = text;
          setCount(countWords(text));
        }}
      ></x-textarea>
      <x-text
        tone=${outOfRange ? 'error' : 'subtle'}
        ${css({ fontSize: '0.85em', alignSelf: 'flex-end' })}
      >
        ${count} Wörter (min. ${min}, max. ${max})
      </x-text>
    </x-flex>
  `;
});

type UploadSlotProps = {
  stContr: GenericFieldFormController<DeutschkursStammdaten>;
  fieldKey: 'b1CertFileID' | 'immaCertFileID';
  label: string;
};

const UploadSlot = stateful<UploadSlotProps>(({ stContr, fieldKey, label }) => {
  const [busy, setBusy] = useState(false);
  const currentFileID = stContr.document?.[fieldKey];

  const pickAndUpload = async () => {
    try {
      const files = await askForFile({ acceptMultipleFiles: false });
      const file = files?.[0];
      if (!file) return;
      if (!file.type?.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
        await XDialog.alert('Bitte nur PDF-Dateien hochladen.');
        return;
      }
      setBusy(true);
      const fileID = await AdornisFile.upload(file);
      if (stContr.document) (stContr.document as any)[fieldKey] = fileID;
      setBusy(false);
      (stContr as any).host?.requestUpdate?.();
    } catch (err) {
      setBusy(false);
      await XDialog.alert(`Fehler beim Upload: ${(err as Error).message}`);
    }
  };

  return html`
    <x-flex space="xs">
      <x-text bold>${label}</x-text>
      <x-flex horizontal space="sm" crossaxis-center wrap>
        ${currentFileID
          ? html`
              <x-link href=${AdornisFile.getServeLink(currentFileID)} target="_blank">
                <tr-button-small icon="description" mode="outline">
                  <x-text>Datei anzeigen</x-text>
                </tr-button-small>
              </x-link>
              <x-link href=${AdornisFile.getDownloadLink(currentFileID)} download>
                <tr-button-small icon="download" mode="outline">
                  <x-text>Herunterladen</x-text>
                </tr-button-small>
              </x-link>
              <tr-button-small
                icon="upload"
                mode="outline"
                .action=${pickAndUpload}
              >
                <x-text>Datei ersetzen</x-text>
              </tr-button-small>
            `
          : html`
              <tr-button-small
                icon="upload"
                .action=${pickAndUpload}
              >
                <x-text>${busy ? 'Lädt hoch…' : 'PDF auswählen'}</x-text>
              </tr-button-small>
            `}
      </x-flex>
    </x-flex>
  `;
});

export const deutschkursRegistrationUserDataPlugin: RegistrationUserDataPlugin<DeutschkursStammdaten> = {
  id: 'registration::user-data::deutschkurs',
  type: 'registration::user-data',
  extraUserFields: [['dateOfBirth']],
  render: (
    stContr: GenericFieldFormController<DeutschkursStammdaten>,
    translationController: TranslationController<BHTranslationDictionary>,
    _user,
    context,
  ) => {
    const t = (key: string) =>
      (translationController.translate('deutschkurs', 'field', key as any) as string) || key;
    const label = (key: string) => translationController.translate('deutschkurs', 'label', key as any) as string;
    const isProfile = context === 'profile';

    return html`
      <x-flex space="lg">
        ${renderIcsrmFields(stContr as unknown as GenericFieldFormController<any>, translationController)}

        <x-flex space="md">
          <tr-h4>Deutschkurs</tr-h4>

          ${isProfile
            ? nothing
            : html`
                ${WordCountedTextarea({
                  stContr,
                  fieldKey: 'motivationText',
                  placeholder: t('motivationText') + '*',
                  min: 100,
                  max: 200,
                })}
                ${WordCountedTextarea({
                  stContr,
                  fieldKey: 'scheduleText',
                  placeholder: t('scheduleText') + '*',
                  min: 50,
                  max: 150,
                })}
              `}

          ${UploadSlot({ stContr, fieldKey: 'b1CertFileID', label: t('b1CertFileID') })}
          ${UploadSlot({ stContr, fieldKey: 'immaCertFileID', label: t('immaCertFileID') })}

          ${isProfile
            ? nothing
            : html`
                <x-checkbox
                  ${stContr.field('acceptedPlacementTest')}
                  .label=${label('acceptedPlacementTest')}
                ></x-checkbox>
                <x-checkbox
                  ${stContr.field('acceptedWeeklyTimeCommitment')}
                  .label=${label('acceptedWeeklyTimeCommitment')}
                ></x-checkbox>
                <x-checkbox
                  ${stContr.field('acceptedAttendancePolicy')}
                  .label=${label('acceptedAttendancePolicy')}
                ></x-checkbox>
              `}
        </x-flex>

        <!-- Wirkungsmessung: Pflichtfeld auf IcsrmStammdaten, das DeutschkursStammdaten erbt -->
        ${renderIcsrmImpactMeasurementSection(
          stContr as unknown as GenericFieldFormController<any>,
          translationController,
        )}
      </x-flex>
    `;
  },
};
